import Dialog from "@/v2-ui/dialog"
import Tooltip from "@/v2-ui/tooltip"
import Button from "@/v2-backoffice-client/components/button"
import { DotText } from "@/v2-backoffice-client/components/dot"
import {
  CRM_STATUS_CREATED,
  CRM_STATUS_FAILED,
  CRM_STATUS_LINKED,
  type CrmOutcome,
} from "@/v2-backoffice-client/crm/crm.constants"
import {
  describeCrmOutcome,
  describeCrmOutcomes,
} from "@/v2-backoffice-client/crm/crm.outcome.utils"
import UserEntraButton from "@/v2-backoffice-client/user/UserEntraButton"
import UserAccountEmailButton from "@/v2-backoffice-client/user/accountEmail/UserAccountEmailButton"
import UserAccountEmailManyButton from "@/v2-backoffice-client/user/accountEmail/UserAccountEmailManyButton"
import type { UserCreateManyMutation } from "@/v2-backoffice-client/user/create/many/__types__/user.create.many.mutation"
import { TooltipLink } from "@/v2-backoffice-client/components/link"

type CreateManyResult = UserCreateManyMutation["createManyUsers"]

export type UserCreateManyPhaseResultProps = {
  result: CreateManyResult
  onClose(): void
}

function crmDotVariant(outcome: CrmOutcome) {
  switch (outcome.Status) {
    case CRM_STATUS_CREATED:
      return "success-1"
    case CRM_STATUS_LINKED:
      return "success-2"
    case CRM_STATUS_FAILED:
      return "destructive"
    default:
      return "muted"
  }
}

function UserCreateManyPhaseResult(props: UserCreateManyPhaseResultProps) {
  const { result, onClose } = props

  const users = result.Users ?? []
  const pwByEmail = new Map(
    (result.GeneratedPasswords ?? []).map((gp) => [gp.Email, gp.Password]),
  )
  const crmOutcomes = result.Crm ?? []
  const crmByEmail = new Map(
    crmOutcomes.map((outcome) => [outcome.Email.toLowerCase(), outcome]),
  )
  const crmSummary = describeCrmOutcomes(crmOutcomes)

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Users Created</Dialog.Title>
        <Dialog.Description>
          {users.length} user(s) created successfully.
          {crmSummary && ` ${crmSummary}.`}
        </Dialog.Description>
      </Dialog.Header>

      {users.length > 0 && (
        <div className="py-4 max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 font-medium">Id</th>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Email</th>
                <th className="pb-2 font-medium">Password</th>
                {crmOutcomes.length > 0 && (
                  <th className="pb-2 font-medium">HubSpot</th>
                )}
                <th className="pb-2 font-medium w-28" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const pw = pwByEmail.get(user.Email ?? "")
                const crm = crmByEmail.get((user.Email ?? "").toLowerCase())
                return (
                  <tr key={user.Id} className="border-b last:border-0">
                    <td className="py-2 text-muted-foreground">{user.Id}</td>
                    <td className="py-2">
                      {user.FirstName} {user.LastName}
                    </td>
                    <td className="py-2">{user.Email}</td>
                    <td className="py-2">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono select-all">
                        {pw ?? "..."}
                      </code>
                    </td>
                    {crmOutcomes.length > 0 && (
                      <td className="py-2 text-xs">
                        {crm && (
                          <Tooltip>
                            <Tooltip.Trigger asChild>
                              <DotText variant={crmDotVariant(crm)}>
                                {crm.Status === CRM_STATUS_CREATED
                                  ? "Created"
                                  : crm.Status === CRM_STATUS_LINKED
                                    ? "Linked"
                                    : crm.Status === CRM_STATUS_FAILED
                                      ? "Failed"
                                      : "Skipped"}
                              </DotText>
                            </Tooltip.Trigger>
                            <Tooltip.Content side="top">
                              {describeCrmOutcome(crm)}
                            </Tooltip.Content>
                          </Tooltip>
                        )}
                      </td>
                    )}
                    <td className="py-2 w-28">
                      <div className="flex items-center gap-px justify-end">
                        <TooltipLink
                          className="inline-block"
                          variant="ghost"
                          iconSrc="user"
                          content="View user"
                          disabled={!user.Id}
                          to={`>>?user=${user.Id}`}
                        />
                        <UserEntraButton azureId={user.AzureID} />
                        <UserAccountEmailButton
                          size="xs"
                          variant="ghost"
                          userId={user.Id}
                          password={pw}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog.Footer className="flex justify-end mt-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {pwByEmail.size > 0 && (
          <UserAccountEmailManyButton
            variant="ghost"
            users={users.map((user) => ({
              userId: user.Id,
              password: pwByEmail.get(user.Email ?? ""),
            }))}
          >
            Send to All
          </UserAccountEmailManyButton>
        )}
      </Dialog.Footer>
    </>
  )
}

export default UserCreateManyPhaseResult
